/* eslint-disable complexity */

import { CurrentUserResult } from '@futrli/views/queries/currentUser';
import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { ExtendedWorkspaceListItem } from '@futrli/views/operations/workspaces/processors/workspacesList';
import {
  getPaymentDetailsPageUrl,
  getRootUrl,
  getSageIdAdditionalInfoUrl,
  matchPaymentDetailsPagePath
} from '@futrli/shared-services';

import { EntryDecision, RENDER } from '../../types';
import {
  getOrganisationUrl,
  getWorkspaceListingUrl,
  getWorkspacesRoutePath,
  matchOrganisationRoutePath,
  matchWorkspaceConsolidationsListingRoutePath,
  matchWorkspaceRoutePath,
  matchWorkspacesRoutePath
} from '../../../utils/route-utils';

export interface WorkspaceEntryDecisionInput {
  workspaces: ExtendedWorkspaceListItem[] | undefined;
  workspacesError: Error | null | undefined;
  currentUser: CurrentUserResult | undefined;
  sbcBaseUrl?: string;
  isDefaultWorkspaceEnabled: boolean;
  pathname: string;
}

/**
 * Returns route data based on the current pathname
 * @param pathname - Current browser url pathname
 */
export const getRouteData = (pathname: string) => {
  const paymentDetailsMatch = matchPaymentDetailsPagePath(pathname);
  const workspaceMatch = matchWorkspaceRoutePath(pathname);

  const workspacesMatchExact = matchWorkspacesRoutePath(pathname, true);
  const workspaceConsolidationsListingMatch = matchWorkspaceConsolidationsListingRoutePath(pathname);
  const workspaceMatchExact = matchWorkspaceRoutePath(pathname, true);
  const organisationMatch = workspaceMatchExact ? null : matchOrganisationRoutePath(pathname, false);

  return {
    isConsolidationsListingPage: !!workspaceConsolidationsListingMatch,
    isPaymentDetailsPage: !!paymentDetailsMatch,
    isRootPage: pathname.split('?')[0] === getRootUrl(),
    isWorkspacesPage: !!workspacesMatchExact,
    isWorkspacePage: !!workspaceMatchExact,
    organisationId: organisationMatch?.params.organisationId || '',
    workspaceId: workspaceMatch?.params.workspaceId || ''
  };
};

/**
 * Will return a redirect to the workspace page, with a query param to add a new org if the workspace has no organisations.
 * @param workspace - The workspace to redirect to
 */
export const getRedirectToWorkspaceResponse = (workspace: ExtendedWorkspaceListItem): EntryDecision => ({
  type: 'navigate',
  target: getWorkspaceListingUrl(workspace.id),
  queryParams: workspace.organisations.length === 0 ? { add_new_org: 'true' } : undefined
});

export const getWorkspaceEntryDecision = ({
  workspaces,
  workspacesError,
  currentUser,
  sbcBaseUrl,
  isDefaultWorkspaceEnabled,
  pathname
}: WorkspaceEntryDecisionInput): EntryDecision => {
  const routeData = getRouteData(pathname);
  const { workspaceId, organisationId } = routeData;

  const workspace = workspaces?.find(w => w.id === workspaceId);
  const organisation =
    workspace && organisationId ? workspace.organisations.find(org => org.id === organisationId) : undefined;
  const consolidation =
    workspace && organisationId ? workspace?.consolidations?.find(c => c.id === organisationId) : undefined;
  const isSageIdUser = !!currentUser?.sageid_id;
  const hasRedirectToPaymentDetailsProperty = currentUser?.properties?.some(
    p => p.property === 'redirect_to_payment_details' && p.value === 'true'
  );

  // ******************************
  // *  Data-loading level rules  *
  // ******************************

  const workspacesErrorStatus = getErrorStatus(workspacesError);
  if (workspacesError && workspacesErrorStatus !== ERROR_STATUS._404) {
    return { type: 'error', error: workspacesError };
  }

  // A 404 from the listing endpoint is equivalent to an empty listing.
  const hasNoWorkspaces = workspacesErrorStatus === ERROR_STATUS._404 || workspaces?.length === 0;
  if (hasNoWorkspaces) {
    if (isSageIdUser) {
      return {
        type: 'navigate',
        target: getSageIdAdditionalInfoUrl()
      };
    }

    return {
      type: 'error',
      error: workspacesError || new Error('No workspaces found for the current user')
    };
  }

  if (!workspaces) {
    return { type: 'error', error: new Error('Unable to load workspaces') };
  }

  // **************************
  // *  / (root) level rules  *
  // **************************

  if (routeData.isRootPage) {
    const selectedWorkspace =
      workspaces.length === 1
        ? workspaces[0]
        : isDefaultWorkspaceEnabled
        ? workspaces.find(w => w.member.is_default_workspace)
        : undefined;

    // If the selected workspace only has 1 organisation, redirect to that organisation page
    if (selectedWorkspace?.organisations.length === 1) {
      return {
        type: 'navigate',
        target: getOrganisationUrl(selectedWorkspace.id, selectedWorkspace.organisations[0].id)
      };
    }

    // If there is a selected workspace (which has more than 1 org), redirect to the workspace page
    if (selectedWorkspace) {
      return getRedirectToWorkspaceResponse(selectedWorkspace);
    }

    // Otherwise, redirect to the workspaces listing page
    return {
      type: 'navigate',
      target: getWorkspacesRoutePath()
    };
  }

  // *****************************
  // *  Workspaces level rules  *
  // *****************************

  if (routeData.isWorkspacesPage) {
    // If the user has only one workspace, redirect to that workspace page
    if (workspaces.length === 1) {
      return getRedirectToWorkspaceResponse(workspaces[0]);
    }
  }

  // ***************************
  // *  Workspace level rules  *
  // ***************************

  if (workspaceId) {
    if (!workspace) {
      return { type: 'navigate', target: getWorkspacesRoutePath() };
    }

    // If user has the redirect_to_payment_details property and owns the workspace, redirect to payment details page if not already there
    if (workspace.flags.isWorkspaceOwner && !routeData.isPaymentDetailsPage && hasRedirectToPaymentDetailsProperty) {
      return { type: 'navigate', target: getPaymentDetailsPageUrl(workspaceId) };
    }

    // Payment details must remain reachable before the first organisation is created.
    if (!routeData.isWorkspacePage && !routeData.isPaymentDetailsPage && workspace.organisations.length === 0) {
      return getRedirectToWorkspaceResponse(workspace);
    }

    // Pure-SFA organisation management is handled by SBC, but consolidations remain managed inside Futrli.
    if (
      workspace.flags.isPureSFA &&
      routeData.isWorkspacePage &&
      !routeData.isConsolidationsListingPage &&
      sbcBaseUrl
    ) {
      return { type: 'external-redirect', target: sbcBaseUrl };
    }
  }

  // **********************************************
  // *  Organisation / consolidation level rules  *
  // **********************************************

  if (workspace && workspaceId && organisationId) {
    // If the orgId doesn't correspond to an org or a consolidation, redirect to the workspace page
    if (!organisation && !consolidation) {
      return getRedirectToWorkspaceResponse(workspace);
    }

    // If the orgId corresponds to an organisation, but the organisation is inactive, redirect to the workspace page
    if (organisation && !organisation.flags.isActiveOrg) {
      return getRedirectToWorkspaceResponse(workspace);
    }
  }

  // If we get here, the user is free to access the workspace and/or organisation, so we can render the page
  return RENDER;
};
